import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Search, CircleCheck, CircleAlert, CircleX, Unlock } from "lucide-react";
import { PacePanel } from "@/components/PacePanel";

const COLOR = {
  VERDE: { cls: "bg-green-100 text-green-700", icon: CircleCheck, label: "Atualizado" },
  AMARELO: { cls: "bg-amber-100 text-amber-700", icon: CircleAlert, label: "Atenção" },
  VERMELHO: { cls: "bg-red-100 text-red-700", icon: CircleX, label: "Pendência" },
};

const ReopenDialog = ({ row, onClose, onDone }) => {
  const [term, setTerm] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setTerm(row?.closed_terms?.length ? String(row.closed_terms[0]) : ""); setReason(""); }, [row]);
  const go = async () => {
    setBusy(true);
    try {
      await api.post("/diary-status/reopen", { class_id: row.class_id, subject_id: row.subject_id, term_order: Number(term), reason });
      toast.success(`Diário reaberto: ${row.subject} — ${row.class_name} (${term}º tri)`); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="reopen-dialog">
        <DialogHeader><DialogTitle className="pr-6">Reabrir diário — {row?.subject} · {row?.class_name}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <Select value={term} onValueChange={setTerm}>
            <SelectTrigger data-testid="reopen-term-select"><SelectValue placeholder="Trimestre" /></SelectTrigger>
            <SelectContent>{(row?.closed_terms || []).map((t) => <SelectItem key={t} value={String(t)}>{t}º Trimestre</SelectItem>)}</SelectContent>
          </Select>
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo da reabertura (obrigatório)" data-testid="reopen-reason" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={go} disabled={busy || !term || !reason.trim()} className="bg-blue-700 hover:bg-blue-800" data-testid="reopen-confirm">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reabrir"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function DiaryPanel() {
  const { scopeParams, selectedSchoolId } = useAuth();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [reopen, setReopen] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => { api.get("/diary-panel", { params: scopeParams }).then((r) => setData(r.data)); /* eslint-disable-next-line */ }, [selectedSchoolId]);
  useEffect(() => { setData(null); load(); }, [load]);

  if (data === null) return <div className="grid h-64 place-items-center"><Loader2 className="h-7 w-7 animate-spin text-blue-700" /></div>;

  const rows = data.rows.filter((r) =>
    (filter === "all" || r.color === filter) &&
    (`${r.class_name} ${r.subject} ${r.teacher}`.toLowerCase().includes(q.toLowerCase())));

  const Stat = ({ label, value, cls }) => (
    <Card className={`p-4 ${cls}`}><div className="font-display text-3xl font-black">{value}</div><div className="text-xs font-semibold">{label}</div></Card>
  );

  return (
    <div>
      <PageHeader title="Acompanhamento dos Diários" subtitle="Situação dos diários por turma, disciplina e professor" />
      <div className="mb-5 grid grid-cols-3 gap-3">
        <Stat label="Atualizados" value={data.counts.updated} cls="bg-green-50 text-green-700 border-green-200" />
        <Stat label="Atenção" value={data.counts.attention} cls="bg-amber-50 text-amber-700 border-amber-200" />
        <Stat label="Pendências" value={data.counts.pending} cls="bg-red-50 text-red-700 border-red-200" />
      </div>

      <PacePanel scopeParams={scopeParams} schoolKey={selectedSchoolId} />

      <div className="mb-4 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="panel-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar turma, disciplina ou professor" className="pl-9" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-full sm:w-48" data-testid="panel-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            <SelectItem value="VERDE">Atualizados</SelectItem>
            <SelectItem value="AMARELO">Atenção</SelectItem>
            <SelectItem value="VERMELHO">Pendências</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="overflow-x-auto border-slate-200" data-testid="panel-table">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Professor</TableHead><TableHead>Turma</TableHead><TableHead>Disciplina</TableHead>
            <TableHead className="text-center">Aulas</TableHead><TableHead>Último registro</TableHead>
            <TableHead className="text-center">Avaliações</TableHead><TableHead>Diário</TableHead><TableHead>Status</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((r, i) => {
              const c = COLOR[r.color];
              return (
                <TableRow key={i} data-testid={`panel-row-${i}`} className="cursor-pointer" onClick={() => navigate(`/diario?class_id=${r.class_id}&subject_id=${r.subject_id}&cn=${encodeURIComponent(r.class_name)}&sn=${encodeURIComponent(r.subject)}`)}>
                  <TableCell className="font-medium text-slate-800">{r.teacher}</TableCell>
                  <TableCell>{r.class_name} <span className="text-xs text-slate-400">{r.grade}</span></TableCell>
                  <TableCell className="text-slate-600">{r.subject}</TableCell>
                  <TableCell className="text-center">{r.lessons_count}</TableCell>
                  <TableCell className="text-slate-500">{r.last_lesson ? new Date(r.last_lesson).toLocaleDateString("pt-BR") : "—"}</TableCell>
                  <TableCell className="text-center">{r.assessments_count}</TableCell>
                  <TableCell>
                    {r.closed_terms?.length ? (
                      <div className="flex items-center gap-1.5">
                        <Badge className="bg-slate-700">Fechado {r.closed_terms.map((t) => `${t}º`).join(", ")}</Badge>
                        <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={(e) => { e.stopPropagation(); setReopen(r); }} data-testid={`panel-reopen-${i}`}><Unlock className="mr-1 h-3.5 w-3.5" />Reabrir</Button>
                      </div>
                    ) : <Badge variant="outline">Aberto</Badge>}
                  </TableCell>
                  <TableCell><span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${c.cls}`}><c.icon className="h-3.5 w-3.5" /> {c.label}</span></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
      <ReopenDialog row={reopen} onClose={() => setReopen(null)} onDone={() => { setReopen(null); load(); }} />
    </div>
  );
}
